import * as React from "react";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { sentry } from "@talentpair/sentry";
import {
  apiRequest,
  useCancelable,
  processPing,
  ProcessResponseT,
  ResponseDataT,
  ProcessT,
} from "@talentpair/api";
import { AppPage, Typography, Box, Button, useMediaContext } from "@talentpair/quantic";
import { isStorageAvailable } from "kyoto/services/storage";
import AuthButtons from "kyoto/components/AuthButtons";
import { UserMessage } from "../../components/UserMessage";
import { AssistantMessage } from "../../components/AssistantMessage";
import { MessageInput } from "../../components/MessageInput";
import logo_url from "../../img/logo-white.png";
import { MessageT } from "../../types";
import { EmptyChatPlaceholder } from "./components/EmptyChatPlaceholder";
import { SessionFeedback } from "./components/SessionFeedback";
import { handleThrottle } from "./throttle";

const CHAT_SESSIONID_KEY = "talentpair_assistant_chat_sessionid";

const can_use_session_storage = isStorageAvailable(window.sessionStorage);

export type TalentpairAssistantChatProps = {
  sessionid: string | null;
};

export default function TalentpairAssistantChat(
  props: TalentpairAssistantChatProps,
): React.ReactElement {
  const { desktop } = useMediaContext();
  // generate a really random number (actually more bits of entropy than a UUIDv4 so don't sweat it!)
  const [sessionid, setSessionId] = React.useState<string | null>(
    props.sessionid ? props.sessionid : can_use_session_storage ? null : "",
  );
  const request = useCancelable();
  const [messages, setMessages] = React.useState<MessageT[]>([]);
  const [bg_process, setBgProcess] = React.useState<string | null>(null);

  const addMessages = React.useCallback(
    (...msgs: MessageT[]) => setMessages((_m) => [..._m, ...msgs]),
    [],
  );

  React.useEffect(() => {
    if (sessionid || (sessionid === null && can_use_session_storage)) {
      let _sessionid = sessionid;
      if (!_sessionid) {
        try {
          _sessionid = sessionStorage.getItem(CHAT_SESSIONID_KEY);
        } catch (err) {
          sentry.warning(err as Error);
        }
      }
      if (_sessionid) {
        if (sessionid !== _sessionid) setSessionId(_sessionid);
        request
          .wrap(apiRequest.get("teleprompter/chat/", { sessionid: _sessionid }))
          .then(
            // @ts-expect-error - it's fine
            (
              res: ResponseDataT<{
                messages: Array<{ content: MessageT }>;
                background_process: ProcessT | null;
              }>,
            ) => {
              if (res.data?.messages.length)
                setMessages(res.data?.messages.map(({ content }) => content));
              if (res.data?.background_process)
                setBgProcess(res.data.background_process.uuid || "");
            },
          )
          .catch((req) => {
            if (req.status) setSessionId("");
            if (handleThrottle(req)) return;
            if (can_use_session_storage) sessionStorage.removeItem(CHAT_SESSIONID_KEY);
          });
      } else setSessionId("");
    }
    // We only want to attempt to restore the sessionid the very first time this component renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // new process id just came in so let's start pinging for it
    if (bg_process) {
      let message_index = 0; // index of latest received message for this BG process
      const p = processPing(
        bg_process,
        {
          timeout: 60_000, // timeout after 1 minute
          interval: 1_000, // ping once every second
          madeProgress: (rsp: ProcessResponseT, prevRsp: ProcessResponseT | null) => {
            const made_progress = !!prevRsp && rsp.data.progress > prevRsp.data.progress;
            if (made_progress) {
              const _messages: MessageT[] = JSON.parse(rsp.data.status_message || "[]");
              const new_messages = _messages.slice(message_index);
              message_index += new_messages.length;
              addMessages(...new_messages);
            }
            return made_progress;
          },
        },
        `teleprompter/chat/processes-uuid/${bg_process}/?sessionid=${sessionid || ""}`,
      );
      p.then((rsp) => {
        const _messages: MessageT[] = JSON.parse(rsp.data.status_message || "[]");
        const new_messages = _messages.slice(message_index);
        addMessages(...new_messages);
        setBgProcess(null);
      }).catch((err) => {
        handleThrottle(err);
        setBgProcess(null);
      });
      return () => p.cancel();
    }
    return () => {};
    // Don't want to rerun this when the `refreshOrg` prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bg_process]);

  const onSubmit = React.useCallback(
    (message: string): Promise<unknown> => {
      let dt = Date.now();
      const msg: MessageT = { role: "hiring manager", message };
      // kick off api request
      return (
        request
          .wrap(
            apiRequest.post("teleprompter/chat/", sessionid ? { sessionid, message } : { message }),
          )
          // @ts-expect-error - it's fine
          .then((res: ProcessResponseT) => {
            // put message in state
            addMessages(msg);
            // if this was the first message then the BE will have generated a sessionid for us
            // save the sessionid in state AND in sessionStorage so it'll persist through a page refresh
            const _sessionid = res.data.subtype;
            setSessionId(_sessionid);
            if (can_use_session_storage) {
              try {
                sessionStorage.setItem(CHAT_SESSIONID_KEY, _sessionid);
              } catch (err) {
                sentry.warning(err as Error);
              }
            }

            dt = Date.now() - dt;
            // delay setting the bg process in state just a tad so that the assistant "feels" a little faster to respond
            setTimeout(setBgProcess, Math.max(500 - dt, 0), res.data.uuid);
          })
          .catch(handleThrottle)
      );
    },
    [request, sessionid, addMessages],
  );

  const last = messages.length - 1;

  const messageControlProps = {
    sessionid: sessionid || "",
    addMessages,
    setBgProcess,
  };

  // prepare the latest messages for logging

  return (
    <AppPage
      logo={<img src={logo_url} alt="Talentpair" style={{ marginBottom: -5, width: 100 }} />}
      leftAction={
        <Button
          variant="outlined"
          icon={RestartAltIcon}
          color="common.white"
          data-testid="resetChatBtn"
          onClick={() => {
            request?.cancel();
            setSessionId("");
            setMessages([]);
            setBgProcess(null);
            if (can_use_session_storage) sessionStorage.removeItem(CHAT_SESSIONID_KEY);
          }}
        >
          Reset{desktop ? " chat" : ""}
        </Button>
      }
      rightAction={<AuthButtons signupOnly={!desktop} />}
    >
      <Box component="section" className="flex-auto" variant="column" layout="stretch-end">
        <div className="flex-auto overflow-auto">
          {messages.length ? (
            <Box
              variant="column"
              layout="stretch-end"
              className="p3 col-12 max-width-3 mx-auto overflow-auto mb4"
            >
              {messages.map((m, i) =>
                m.role === "Perry" ? (
                  <AssistantMessage {...m} last={i === last} {...messageControlProps} />
                ) : (
                  <UserMessage {...m} last={i === last} />
                ),
              )}
              {bg_process ? <AssistantMessage typing last {...messageControlProps} /> : null}
            </Box>
          ) : sessionid === "" ? (
            <EmptyChatPlaceholder desktop={desktop} onSubmit={onSubmit} />
          ) : null}
        </div>
        <div className="px3 col-12 max-width-3 mx-auto">
          <MessageInput key={sessionid || "foo"} disabled={!!bg_process} onSubmit={onSubmit} />
        </div>
        <Box variant="row" layout="center-center" space={1} className="my1" style={{ height: 32 }}>
          <Typography variant="body2" color="grey.700" align="center">
            Assistant is in beta.
          </Typography>
          <Typography variant="body2" color="grey.700" align="center">
            Chats may be imperfect.
          </Typography>
          {sessionid ? (
            <SessionFeedback key={sessionid} sessionid={sessionid} messages={messages} />
          ) : null}
        </Box>
      </Box>
    </AppPage>
  );
}
